import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../Common/PageHeader/PageHeader';
import { getPaymentsFull } from '../../services/adminApi';
import { updateDocumentTitle } from '../../utils/documentTitle';
import './AdminHubPage.css';
import './AdminPaymentsPage.css';

/**
 * AdminPaymentsPage: /admin/payments (Nitish, Oct 19 2026): "who's paying, and how?", weekly, the last 12 ET weeks.
 * Reads the hourly cache only (payments_report_service.collect_payments_full on recompute_live_metrics; 503 when cold
 * or stale, same as the scoreboard). Every definition lives server-side in that module's docstring; this page only
 * draws. Same SVG idiom as AdminScoreboardPage (no chart library, hover or tap for the numbers), with the labels in
 * HTML so they never stretch on a phone, and a table under every chart.
 */
const etTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'; } catch (e) { return ''; }
};
const ageText = (d) => {
  if (!d) return '';
  let secs = null;
  if (d.cache && typeof d.cache.age_seconds === 'number') secs = d.cache.age_seconds;
  else if (d.computed_at) secs = Math.max(0, (Date.now() - new Date(d.computed_at).getTime()) / 1000);
  if (secs === null || Number.isNaN(secs)) return 'fresh';
  const mins = Math.round(secs / 60);
  return mins < 1 ? 'computed just now' : `computed ${mins} min ago (${etTime(d.computed_at || (d.cache && d.cache.computed_at))})`;
};

export const fmtUsd = (v, compact) => {
  if (typeof v !== 'number') return '—';
  if (compact && v >= 1000) return `$${(Math.round(v / 100) / 10).toLocaleString()}k`;
  return `$${Math.round(v).toLocaleString()}`;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const weekLabel = (startIso) => {
  const [, m, d] = String(startIso || '').split('-').map(Number);
  return m && d ? `${MONTHS[m - 1]} ${d}` : String(startIso || '');
};

const S = (n) => `var(--pay-${n})`;
const NONE = 'var(--pay-none)';
const S_WHO = [
  { k: 'offer', label: 'New subscription, first-month offer', color: S(1), hatch: true },
  { k: 'new_other', label: 'New subscription, full price', color: S(1) },
  { k: 'upgrade', label: 'Upgrade', color: S(2) },
  { k: 'renewal', label: 'Renewal', color: S(3) },
  { k: 'topup_only', label: 'Top-up only', color: S(4) },
  { k: 'auto_recharge', label: 'Auto-recharge', color: S(5) },
];
const S_PATH = [
  { k: 'mcp', label: 'Claude / MCP', color: S(1) },
  { k: 'api_key', label: 'API key / plugin', color: S(2) },
  { k: 'invite', label: 'Invite', color: S(3) },
  { k: 'web', label: 'Web', color: S(4) },
];
const S_COUNTRY = [
  { k: 'us', label: 'US', color: S(1) },
  { k: 'paying', label: 'Paying countries', color: S(2) },
  { k: 'restricted', label: 'Restricted', color: S(3) },
  { k: 'other', label: 'Other', color: S(4) },
  { k: 'unknown', label: 'Unknown', color: NONE },
];
const FUNNEL = [
  { k: 'signups', label: 'Signups' },
  { k: 'ran_out', label: 'Ran out of credits' },
  { k: 'seen', label: 'Saw the offer' },
  { k: 'clicked', label: 'Clicked a plan' },
  { k: 'paid_after', label: 'Paid after running out' },
];

/** The cached payload -> chart rows, oldest week first. Exported for the test. */
export const buildRows = (data) => {
  const weeks = (data && data.weeks) || [];
  const base = (w) => ({ key: w.key, label: weekLabel(w.start), tip: `Week of ${weekLabel(w.start)}${w.partial ? ' (so far)' : ''}`, start: w.start });
  const who = weeks.map((w) => {
    const r = (data.who_paid || {})[w.key] || {};
    const offer = r.new_sub_offer || 0;
    return {
      ...base(w), offer, new_other: Math.max(0, (r.new_sub || 0) - offer), upgrade: r.upgrade || 0, renewal: r.renewal || 0,
      topup_only: r.topup_only || 0, auto_recharge: r.auto_recharge || 0, dollars: r.dollars || 0, unpriced: r.payments_unpriced || 0,
    };
  });
  const pick = (block) => weeks.map((w) => ({ ...base(w), ...(((data[block] || {})[w.key]) || {}) }));
  return { who, path: pick('new_payers_by_path'), country: pick('new_payers_by_country'), funnel: pick('offer_funnel') };
};

const W = 560; const H = 150; const PAD = { l: 26, r: 4, t: 6, b: 4 }; // l = the gutter under the HTML y-max label
const IW = W - PAD.l - PAD.r; const IH = H - PAD.t - PAD.b;
const pctX = (i, n) => ((PAD.l + (i + 0.5) * (IW / n)) / W) * 100;

const Swatch = ({ color }) => (<svg className="pay-swatch" width="10" height="10" aria-hidden="true" style={{ marginRight: 5, verticalAlign: -1 }}><rect width="10" height="10" rx="2" style={{ fill: color }} /></svg>);

const Tip = ({ leftPct, title, items, extra }) => (
  <div className="adm-tip" style={{ left: `${Math.min(84, Math.max(16, leftPct))}%` }} role="status">
    <div className="adm-tip-h">{title}</div>
    {items.map((it) => (
      <div className="adm-tip-r" key={it.label}><Swatch color={it.color} />{it.label}<b>{(it.v || 0).toLocaleString()}</b></div>
    ))}
    {items.length > 1 ? <div className="adm-tip-r adm-tip-t">People<b>{items.reduce((s, it) => s + (it.v || 0), 0).toLocaleString()}</b></div> : null}
    {extra ? extra.map((x) => <div className="adm-tip-r" key={x.label}>{x.label}<b>{x.v}</b></div>) : null}
  </div>
);

const Legend = ({ rows, series }) => (
  <div className="adm-legend">
    {series.map((s) => (
      <span key={s.k}>
        {s.hatch ? <i className="pay-hatched" /> : <Swatch color={s.color} />}{s.label}
        <b className="adm-legend-n">{rows.reduce((t, r) => t + (r[s.k] || 0), 0).toLocaleString()}</b>
      </span>
    ))}
  </div>
);

const Strip = ({ rows, text, className = '' }) => (
  <div className={`pay-strip ${className}`.trim()} aria-hidden="true">
    {rows.map((r, i) => <span key={r.key} className={(rows.length - 1 - i) % 2 ? 'pay-odd' : ''} style={{ left: `${pctX(i, rows.length)}%` }}>{text(r)}</span>)}
  </div>
);

/* Stacked weekly bars: 2px surface gap between segments, hatched overlay for a subset segment. */
const StackBars = ({ rows, series, dollars, label }) => {
  const [hi, setHi] = useState(null);
  if (!rows.length) return null;
  const tot = (r) => series.reduce((s, x) => s + (r[x.k] || 0), 0);
  const max = Math.max(1, ...rows.map(tot));
  const bw = IW / rows.length;
  const y = (v) => PAD.t + IH - (v / max) * IH;
  const hr = hi !== null ? rows[hi] : null;
  return (
    <div onMouseLeave={() => setHi(null)}>
      {dollars ? <Strip rows={rows} className="pay-dl" text={(r) => (r.dollars ? fmtUsd(r.dollars, true) : '')} /> : null}
      <div className="adm-chart-wrap">
        <span className="pay-ymax">{max}</span>
        <svg className="adm-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
          {series.some((s) => s.hatch) ? (
            <defs>
              <pattern id="pay-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line className="pay-hatch-line" x1="0" y1="0" x2="0" y2="6" />
              </pattern>
            </defs>
          ) : null}
          <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={PAD.t + IH} y2={PAD.t + IH} />
          {rows.map((r, i) => {
            const x = PAD.l + i * bw + bw * 0.18; const w = Math.max(1, bw * 0.64);
            let acc = 0;
            return (
              <g key={r.key} className={hi === null ? '' : hi === i ? 'on' : 'dim'}>
                {series.map((s) => {
                  const v = r[s.k] || 0;
                  if (!v) return null;
                  const y0 = y(acc); acc += v; const y1 = y(acc);
                  const h = Math.max(0, y0 - y1 - 2);
                  return (
                    <React.Fragment key={s.k}>
                      <rect className="seg" x={x} width={w} y={y1} height={h} style={{ fill: s.color }} />
                      {s.hatch ? <rect x={x} width={w} y={y1} height={h} fill="url(#pay-hatch)" /> : null}
                    </React.Fragment>
                  );
                })}
                <rect className="hit" x={PAD.l + i * bw} width={bw} y={PAD.t} height={IH} onMouseEnter={() => setHi(i)} onClick={() => setHi(i)} />
              </g>
            );
          })}
        </svg>
        {hr ? (
          <Tip leftPct={pctX(hi, rows.length)} title={hr.tip}
            items={series.map((s) => ({ label: s.label, color: s.color, v: hr[s.k] || 0 }))}
            extra={dollars ? [{ label: 'Dollars (recorded)', v: fmtUsd(hr.dollars) }, ...(hr.unpriced ? [{ label: 'Payments with no recorded amount', v: hr.unpriced }] : [])] : null} />
        ) : null}
      </div>
      <Strip rows={rows} text={(r) => r.label} />
    </div>
  );
};

const TableView = ({ rows, cols }) => (
  <details className="pay-table-wrap">
    <summary>Table</summary>
    <div className="pay-table-scroll">
      <table className="pay-table">
        <thead><tr><th>Week of</th>{cols.map((c) => <th key={c.k}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.slice().reverse().map((r) => (
            <tr key={r.key}><td>{r.label}</td>{cols.map((c) => <td key={c.k}>{c.fmt ? c.fmt(r[c.k]) : (r[c.k] || 0).toLocaleString()}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);

const pctOf = (n, d) => (d ? `${Math.round((100 * n) / d)}%` : '');

const Funnel = ({ totals }) => {
  const top = Math.max(1, totals.signups || 0);
  return (
    <div className="pay-funnel">
      {FUNNEL.map((st, i) => {
        const v = totals[st.k] || 0;
        const prev = i ? totals[FUNNEL[i === 4 ? 1 : i - 1].k] || 0 : 0;
        return (
          <div className="pay-funnel-row" key={st.k}>
            <span className="pay-funnel-k">{st.label}</span>
            <div><div className="pay-funnel-bar" style={{ width: `${(100 * v) / top}%` }} /></div>
            <span className="pay-funnel-v">{v.toLocaleString()}{i ? <small>{pctOf(v, prev)}</small> : null}</span>
          </div>
        );
      })}
    </div>
  );
};

const AdminPaymentsPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const res = await getPaymentsFull();
      setData(res.data);
    } catch (e) {
      setError(String(e?.response?.data?.detail || e?.message || 'unavailable'));
    }
    setBusy(false);
  }, []);
  useEffect(() => { updateDocumentTitle('Payments · Admin'); load(); }, [load]);

  const rows = useMemo(() => (data ? buildRows(data) : null), [data]);
  const funnelTotals = useMemo(() => {
    if (!rows) return null;
    // Signup weeks from the one the offer went live in (seen / clicked do not exist before it).
    const live = (data && data.offer_live_since) || '';
    const weeks = rows.funnel.filter((r) => {
      const end = new Date(`${r.start}T00:00:00Z`); end.setUTCDate(end.getUTCDate() + 6);
      return !live || end.toISOString().slice(0, 10) >= live;
    });
    const t = {};
    FUNNEL.concat([{ k: 'offer_converted' }]).forEach((st) => { t[st.k] = weeks.reduce((s, r) => s + (r[st.k] || 0), 0); });
    t.from = weeks.length ? weeks[0].label : null;
    return t;
  }, [rows, data]);

  const tiles = useMemo(() => {
    if (!data || !data.tiles || !rows) return null;
    const t = data.tiles;
    const n = rows.who.length;
    const payers = (r) => (r ? S_WHO.reduce((s, x) => s + (r[x.k] || 0), 0) : 0);
    return [
      { k: 'New payers · this week', v: t.new_payers_this_week, d: `vs ${t.new_payers_last_week} last week · first payment of any kind, ever` },
      { k: 'Dollars · this week', v: fmtUsd(t.dollars_this_week), d: `vs ${fmtUsd(t.dollars_last_week)} last week · before tax${t.unpriced_this_week ? ` · ${t.unpriced_this_week} payment${t.unpriced_this_week === 1 ? '' : 's'} with no recorded amount` : ''}` },
      { k: 'People who paid · this week', v: payers(rows.who[n - 1]), d: `vs ${payers(rows.who[n - 2])} last week · any payment, one bucket each` },
    ];
  }, [data, rows]);

  const ex = (data && data.excluded) || null;
  return (
    <div className="adm-page pay-page">
      <p className="adm-crumb"><Link to="/admin">Admin</Link> › Payments</p>
      <PageHeader
        title="Payments"
        subtitle="Who's paying, and how. People, not events; Mon-Sun ET weeks; internal, test, duplicate and throwaway accounts left out."
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={busy}>Refresh</Button>}
      />
      <p className="adm-stamp">
        {data ? <span>Hourly cache · {ageText(data)} · last 12 weeks, this week so far · hover or tap a chart for exact numbers</span> : <span>{error ? 'Not serving' : 'Loading…'}</span>}
      </p>
      {error && !data ? <div className="adm-empty">The payments cache is not serving: {error}</div> : null}
      {tiles ? (
        <div className="adm-tiles">
          {tiles.map((t) => (
            <div className="adm-tile" key={t.k}><div className="adm-tile-k">{t.k}</div><div className="adm-tile-v">{t.v}</div><div className="adm-tile-d">{t.d}</div></div>
          ))}
        </div>
      ) : null}
      {rows ? (
        <div className="adm-panels">
          <div className="adm-panel">
            <h3 className="adm-panel-h">Who paid, weekly</h3>
            <p className="adm-panel-s">People, one bucket each: new subscription &gt; upgrade &gt; renewal &gt; top-up &gt; auto-recharge. Dollars above each bar are the recorded Stripe amounts before tax; first-month-offer subscriptions record no charge and are left out of them.</p>
            <StackBars rows={rows.who} series={S_WHO} dollars label="People who paid per week, by kind" />
            <Legend rows={rows.who} series={S_WHO} />
            <TableView rows={rows.who} cols={[...S_WHO.map((s) => ({ k: s.k, label: s.label })), { k: 'dollars', label: 'Dollars', fmt: (v) => fmtUsd(v) }, { k: 'unpriced', label: 'No amount' }]} />
          </div>
          <div className="adm-panel">
            <h3 className="adm-panel-h">New payers by signup path, weekly</h3>
            <p className="adm-panel-s">People whose first payment ever fell in the week. Claude / MCP &gt; API key &gt; invite &gt; web.</p>
            <StackBars rows={rows.path} series={S_PATH} label="New payers per week, by signup path" />
            <Legend rows={rows.path} series={S_PATH} />
            <TableView rows={rows.path} cols={S_PATH} />
          </div>
          <div className="adm-panel">
            <h3 className="adm-panel-h">New payers by country, weekly</h3>
            <p className="adm-panel-s">Restricted = the reduced-starter-grant list. Paying = a real payer from there in the 90 days before the week.</p>
            <StackBars rows={rows.country} series={S_COUNTRY} label="New payers per week, by country bucket" />
            <Legend rows={rows.country} series={S_COUNTRY} />
            <TableView rows={rows.country} cols={S_COUNTRY} />
          </div>
          <div className="adm-panel">
            <h3 className="adm-panel-h">First-month offer funnel, by signup week</h3>
            <p className="adm-panel-s">
              {funnelTotals && funnelTotals.from ? `Signups from the week of ${funnelTotals.from} on (the offer went live ${weekLabel(data.offer_live_since)}). ` : ''}
              Ran out = the balance hit zero before any payment; paid = any payment after that ({funnelTotals ? funnelTotals.offer_converted : 0} through the offer). Percent is of the step above; paid is of ran out.
            </p>
            {funnelTotals ? <Funnel totals={funnelTotals} /> : null}
            <TableView rows={rows.funnel} cols={[...FUNNEL, { k: 'offer_converted', label: 'Via offer' }]} />
            {/* Nitish, Oct 20 2026: the funnel groups by SIGNUP week, so a week's offer buyers who signed up earlier sit in
                older rows; this line counts them by the week they paid (who_paid.new_sub_offer). */}
            <p className="adm-panel-s" data-testid="offer-payments-week">
              Offer payments this week, by payment date: {rows.who.length ? rows.who[rows.who.length - 1].offer : 0}
            </p>
          </div>
        </div>
      ) : null}
      {ex ? (
        <p className="pay-note">
          Left out of every count in these 12 weeks: {ex.org_wallet_payments} organization-wallet payment{ex.org_wallet_payments === 1 ? '' : 's'} (no single person), {ex.internal_or_test_payers} internal or test payer{ex.internal_or_test_payers === 1 ? '' : 's'}, {ex.duplicate_payers} duplicate payer{ex.duplicate_payers === 1 ? '' : 's'}, {ex.throwaway_payers ?? 0} throwaway payer{ex.throwaway_payers === 1 ? '' : 's'}.
        </p>
      ) : null}
    </div>
  );
};

export default AdminPaymentsPage;
